import { onValue, Unsubscribe } from 'firebase/database'
import { onSnapshot } from 'firebase/firestore'

import { VerticalPointerSize } from '../components/NavigationPointers/NavigationPointers'
import TvEventCard from '../components/TvCards/TvEventCard'
import TvCarouselContainer from '../components/TvCarouselContainer/TvCarouselContainer'
import { FALLBACK_MESSAGE_ITEM } from '../components/TvRowsContainer/TvRowsContainer'
import { AppConfig } from '../services/appConfig'
import {
  CardItemModel,
  FirebaseEventUpdate,
  SportData,
  StreamItem,
  TvBrowseLayoutModel,
  TvCarouselContainerModel,
} from '../types/flo-experience'
import { generateBFFCarousel, getUpdatedCardItems } from '../utils/cardUtils'
import {
  areStreamsEqual,
  Firebase,
  getLatestDocs,
  streamIdsArr,
  streamsArr,
} from '../utils/firebase'
import { areObjsEqual, chunkArray } from '../utils/misc'
import { genericError, networkError } from '../widgets/modal'
import CarouselsHandler from './handlers/carouselsHandler'

export default class EventHub extends CarouselsHandler {
  private _data: TvBrowseLayoutModel
  private _firebase: Firebase = new Firebase()
  private _eventListener: Unsubscribe
  private _sportDataListeners: Unsubscribe[] = []
  private _latestStreams: StreamItem[] = []
  private _latestSportData: SportData[] = []
  private _latestStatus: string
  private _streamsCarousel: TvCarouselContainer
  private _ready = false

  _init() {
    this._firebase.init()

    this.tvBrowseLayout.isEventHub = true
    this.navigationPointers.verticalPointerSize = VerticalPointerSize.SMALL
  }

  get pageText() {
    return 'Event Hub'
  }

  _inactive() {
    if (this._eventListener) {
      this._eventListener()
    }

    this.stopListeningToSportsData()
  }

  async _onUrlParams(args) {
    this._setState('NotReady')

    await this.getEventHubData(args.url)
  }

  async getEventHubData(url) {
    const data = await AppConfig.shared.experienceApi.getEventHub(url)

    if (data?.error) {
      const error = data.isNetworkError ? networkError : genericError
      this.setModal(true, error)
      return
    }

    this._data = data
    this._data.container.items =
      this._lastCarousels.length > 0 ? this._lastCarousels : this._data.container.items
    this.tvBrowseLayout.data = this._data
    this.tvBrowseLayout.spotlight.renderSpotlightData({ spotlight_metadata: this._data.spotlight })

    this.restoreFocus()

    if (this._data?.content_metadata?.live_id) {
      this.listenToEventUpdates(this._data.content_metadata.live_id)
    } else {
      this.setEventHubReady()
    }
  }

  listenToEventUpdates(liveId: number) {
    this._eventListener = onValue(
      this._firebase.eventDbRef(liveId),
      (snapshot) => {
        const data = snapshot.val()

        if (data) {
          this.onFirebaseChange(data, liveId)
        }
      },
      (error) => {
        console.error(error)
        this.setEventHubReady()
      },
    )
  }

  onFirebaseChange(data: FirebaseEventUpdate, liveId: number) {
    console.debug('Real time db update: ', data)

    this.tvBrowseLayout.spotlight.metadata.updateBadge(data?.status_v2)

    if (!this._ready) {
      this.setEventHubReady()
    }

    if (data?.status_v2 === 'CONCLUDED' || !data?.streams) {
      const carousels = this.getRemainingCarousels()

      if (carousels.length > 0) {
        this.tvBrowseLayout.tvRowsContainer.data = carousels
      } else {
        this.tvBrowseLayout.tvRowsContainer.data = [FALLBACK_MESSAGE_ITEM]
      }

      this.tvBrowseLayout.cancelSpotlight()
      this._refocus()
    }

    if (data?.streams && data?.status_v2 === 'LIVE') {
      this._latestStatus = data?.status_v2
      let previousStreams = this._latestStreams
      this._latestStreams = streamsArr(data?.streams)

      // Only update if streams change, either new ones are added or removed
      if (!areStreamsEqual(previousStreams, this._latestStreams)) {
        this.updateViewWithLatestStreams()
        this.startListeningToSportsData(this._latestStreams, liveId)
      }
    }
  }

  startListeningToSportsData(streams, liveId) {
    this.stopListeningToSportsData()

    const firebaseMaxWhereIn = 10
    const slices = chunkArray(streams, firebaseMaxWhereIn)

    slices.forEach((slice) => {
      const streamIds = streamIdsArr(slice)
      const q = this._firebase.query(streamIds, liveId)

      streamIds.forEach(() => {
        const unsubscribe = onSnapshot(q, (snapshot) => {
          if (snapshot !== null) {
            const data = getLatestDocs(snapshot)
            let previousData = this._latestSportData
            this._latestSportData = data

            if (!areObjsEqual(previousData, this._latestSportData)) {
              console.debug('Latest sport data: ', data)

              this.processSportsDataUpdate(data)
            }
          }
        })

        this._sportDataListeners.push(unsubscribe)
      })
    })
  }

  stopListeningToSportsData() {
    if (this._sportDataListeners.length > 0) {
      this._sportDataListeners.forEach((unsub: Unsubscribe) => unsub?.())
    }
  }

  updateStreams(items: CardItemModel[]) {
    try {
      this._streamsCarousel.updateCards(items)
    } catch (e) {
      console.error(e)
    }
  }

  processSportsDataUpdate(sportData: SportData[]) {
    sportData.forEach((data: SportData) => {
      const card = this._streamsCarousel.scrollContainer.children.find(
        (item: TvEventCard) => item.id === data.identity.liveStreamId,
      ) as TvEventCard

      if (card) {
        card.updateSportsData(data)
      }
    })
  }

  updateViewWithLatestStreams() {
    // Save component reference to update children
    // and pass the most updated reference to build the cards
    const streamsCarousel = this.tvBrowseLayout.tvRowsContainer.carouselsContainer.children.find(
      (c: TvCarouselContainer) => c?.data?.title?.text.includes('Streams'),
    ) as TvCarouselContainer

    // According to FloSports there's a weird but possible scenario where
    // the event hub could go from 'No content' state to get streams from Firebase
    // in this case we need to generate the TvCarouselContainer model in our end
    // and insert the streams
    if (!streamsCarousel) {
      this.tvBrowseLayout.tvRowsContainer.carouselsContainer.childList.clear()
      const bffCarousel = generateBFFCarousel()
      const items = getUpdatedCardItems(
        this._latestSportData,
        this._latestStreams,
        this._latestStatus,
        this._data,
      )
      bffCarousel.items = items
      this.tvBrowseLayout.tvRowsContainer.data = [bffCarousel, ...this.getRemainingCarousels()]
      return
    }

    this._streamsCarousel = streamsCarousel

    const updatedStreams = getUpdatedCardItems(
      this._latestSportData,
      this._latestStreams,
      this._latestStatus,
      this._data,
      streamsCarousel,
    )

    this.updateStreams(updatedStreams)
  }

  getRemainingCarousels() {
    const carousels = this._data?.container?.items.filter(
      (item: TvCarouselContainerModel) => !item?.title?.text.includes('Streams'),
    )

    return carousels ?? []
  }

  setEventHubReady() {
    // Small hack to wait for SpeechComp to get the latest labels
    // because if the event is live we update the carousel a second time
    // with the streams from Firebase thus the focus is triggered again
    setTimeout(() => {
      this._ready = true
      this._setState('Ready')
    }, 0)
  }
}
